import type { TenantModules } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { bpsToPercentText, percentToBps } from "../lib/tenantModulesForm";
import { useTRPC } from "../lib/trpc";
import { CommercialTermsFields, TenantModulesFields } from "./TenantModulesFields";

/** A tenant as `platform.tenants.list` returns it: the fields this dialog edits. */
export interface TenantSettingsTarget {
  id: string;
  name: string;
  archivedAt: string | null;
  moduleSwitches: TenantModules;
  storefrontProfitShareBps: number;
  shopifyPlatformShareBps: number;
}

/**
 * The tenant's modules and revenue shares, edited from the Tenants list without opening the
 * tenant: the same two writes as its Settings page (`setModules`, `setCommercialTerms`), each
 * sent only when its part changed. Mounted per open (keyed by the caller), so a reopen starts from
 * what the list last read. An archived tenant is read-only here, as the server refuses the save.
 */
export function TenantSettingsDialog({
  tenant,
  onClose,
}: {
  tenant: TenantSettingsTarget;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [modules, setModules] = useState(tenant.moduleSwitches);
  const [storefrontShare, setStorefrontShare] = useState(
    bpsToPercentText(tenant.storefrontProfitShareBps),
  );
  const [shopifyShare, setShopifyShare] = useState(
    bpsToPercentText(tenant.shopifyPlatformShareBps),
  );
  const setModulesMutation = useMutation(trpc.platform.tenants.setModules.mutationOptions());
  const setTermsMutation = useMutation(trpc.platform.tenants.setCommercialTerms.mutationOptions());

  const storefrontBps = percentToBps(storefrontShare);
  const shopifyBps = percentToBps(shopifyShare);
  const sharesInvalid = storefrontBps === null || shopifyBps === null;
  const modulesChanged = JSON.stringify(modules) !== JSON.stringify(tenant.moduleSwitches);
  const termsChanged =
    storefrontBps !== tenant.storefrontProfitShareBps ||
    shopifyBps !== tenant.shopifyPlatformShareBps;
  const pending = setModulesMutation.isPending || setTermsMutation.isPending;
  const archived = tenant.archivedAt !== null;

  const save = async () => {
    if (storefrontBps === null || shopifyBps === null) return;
    // A failure is toasted by the query client; the dialog stays open on the draft.
    if (modulesChanged) await setModulesMutation.mutateAsync({ tenantId: tenant.id, modules });
    if (termsChanged) {
      await setTermsMutation.mutateAsync({
        tenantId: tenant.id,
        storefrontProfitShareBps: storefrontBps,
        shopifyPlatformShareBps: shopifyBps,
      });
    }
    void queryClient.invalidateQueries({
      queryKey: trpc.platform.tenants.list.queryOptions().queryKey,
    });
    void queryClient.invalidateQueries({ queryKey: trpc.tenant.get.queryOptions().queryKey });
    toast.success(`Settings saved for ${tenant.name}.`);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{tenant.name} settings</DialogTitle>
          <DialogDescription>
            {archived
              ? "This tenant is archived; restore it to change its settings."
              : "What the tenant can use, and the platform's revenue shares."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            save().catch(() => {});
          }}
        >
          <fieldset className="grid gap-2">
            <legend className="mb-2 text-sm font-medium">Modules</legend>
            <TenantModulesFields
              idPrefix={`row-module-${tenant.id}`}
              value={modules}
              onChange={setModules}
              disabled={archived || pending}
            />
          </fieldset>
          <fieldset className="grid gap-2">
            <legend className="mb-2 text-sm font-medium">Revenue shares</legend>
            <p className="text-sm text-muted-foreground">
              The storefront share is for reporting only. Loot Boxes for Shopify bills the Shopify
              fee as usage charges on the merchant's Shopify invoice.
            </p>
            <CommercialTermsFields
              idPrefix={`row-${tenant.id}`}
              storefrontProfitShare={storefrontShare}
              shopifyPlatformShare={shopifyShare}
              onStorefrontProfitShareChange={setStorefrontShare}
              onShopifyPlatformShareChange={setShopifyShare}
              disabled={archived || pending}
            />
            {sharesInvalid && (
              <p className="text-sm text-destructive">
                Shares are percents from 0 to 100, to two decimals.
              </p>
            )}
          </fieldset>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={archived || pending || sharesInvalid || (!modulesChanged && !termsChanged)}
            >
              {pending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
